import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calendar, MapPin, ArrowLeft } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

const EventDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    loadEvent();
  }, [slug]);

  // Update title when event loads
  useEffect(() => {
    if (event) {
      document.title = `${event.title} - ${settings.site_name || 'Cinefora'}`;
    }
  }, [event, settings.site_name]);

  const loadEvent = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/events/slug/${slug}`);
      setEvent(response.data);
    } catch (error) {
      console.error('Event load error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto px-4 py-32">
          <div className="text-white text-center">Yükleniyor...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto px-4 py-32">
          <div className="text-white text-center">Haber bulunamadı</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <div className="pt-32 pb-16">
        <div className="container mx-auto px-4">
          {/* Back Button */}
          <button
            onClick={() => navigate('/haberler')}
            className="flex items-center text-[#FDB714] hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Haberlere Dön
          </button>

          {/* Hero Image */}
          <div className="relative w-full h-[400px] rounded-lg overflow-hidden mb-8">
            <img
              src={event.image}
              alt={event.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
          </div>

          {/* Content */}
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {event.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-gray-400 mb-8">
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>{event.event_date}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={18} />
                <span>{event.location}</span>
              </div>
            </div>

            {/* Short Description */}
            <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-6 text-gray-300 text-lg mb-8 leading-relaxed">
              {event.description}
            </div>

            {/* Rich Content */}
            {event.content && (
              <div 
                className="prose prose-invert prose-lg max-w-none text-white"
                dangerouslySetInnerHTML={{ __html: event.content }}
                style={{
                  fontSize: '1.125rem',
                  lineHeight: '1.75',
                }}
              />
            )}
          </div>
        </div>
      </div>

      <Footer />
      
      <style jsx>{`
        .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
          color: #FDB714;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        .prose p {
          margin-bottom: 1rem;
        }
        .prose ul, .prose ol {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
        }
        .prose li {
          margin-bottom: 0.5rem;
        }
        .prose img {
          border-radius: 0.5rem;
          margin: 2rem 0;
        }
        .prose a {
          color: #FDB714;
          text-decoration: underline;
        }
        .prose a:hover {
          color: #e5a510;
        }
      `}</style>
    </div>
  );
};

export default EventDetail;
